import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, map } from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { Users } from 'server/shared/collections/users';
import { Customer } from 'server/shared/models/customer';
import { DispoUser } from 'server/shared/models/user';
import { PoolerService } from '../../../services/pooler/pooler.service';
import { ToastService } from '../../../services/toast/toast.service';
@Component({
  selector: 'app-user-management-filter',
  templateUrl: './user-management-filter.component.html',
  styleUrls: ['./user-management-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserManagementFilterComponent implements OnInit, OnDestroy {
  loggedInUser$ = new BehaviorSubject<DispoUser | undefined>(undefined);
  loggedInCustomer$ = new BehaviorSubject<Customer | undefined>(undefined);
  hasAgencies$ = new BehaviorSubject<boolean>(false);

  dateRangeChips$ = new BehaviorSubject<string[]>([
    'ALL',
    'N30',
    'N90',
    'L30N30',
    'L90',
    'CUSTOM',
  ]);

  filterCollapsed$ = new BehaviorSubject<boolean>(true);

  private userTracker?: Tracker.Computation;

  filterCount$ = this.route.queryParams.pipe(
    map((params) => {
      const filterParams = [
        'search',
        'restrictToCustomerIds',
        'certificates',
        'department',
        'specialities',
      ];
      return filterParams.reduce((count, param) => {
        return params[param] ? count + 1 : count;
      }, 0);
    }),
  );

  constructor(
    private ngZone: NgZone,
    private poolerService: PoolerService,
    private route: ActivatedRoute,
    private router: Router,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.userTracker = Tracker.autorun(() => {
      const loggedInUser = Users.findOne(Meteor.userId()) as DispoUser;
      const loggedInCustomer = Customers.findOne(loggedInUser?.customerId);

      this.loggedInUser$.next(loggedInUser);
      this.loggedInCustomer$.next(loggedInCustomer);

      let sharedPoolCustomers = Customers.find({
        $or: [
          {
            'pool.jobsSharedWithCustomerIds': loggedInCustomer?._id,
          },
          {
            'pool.employmentsSharedWithCustomerIds': loggedInCustomer?._id,
          },
        ],
      }).fetch();

      if (
        sharedPoolCustomers?.length ||
        loggedInCustomer?.customerTransfers?.transfers?.length ||
        loggedInCustomer?.customerTransfers?.employmentTransfers?.length
      ) {
        this.hasAgencies$.next(true);
      } else {
        this.hasAgencies$.next(false);
      }

      if (loggedInUser?.jobAvailabilitiesOverviewFilter) {
        this._loadDefaultFilters();
      }
    });
  }

  ngOnDestroy() {
    if (this.userTracker) {
      this.userTracker.stop();
    }
  }

  saveDefaultFilter() {
    const loggedInUser = this.loggedInUser$.value;
    if (!loggedInUser) return;
    Meteor.call(
      'saveJobAvailabilitiesOverviewFilter',
      this.route.snapshot.queryParams,
    );
    if (!loggedInUser.jobAvailabilitiesOverviewFilter) {
      loggedInUser.jobAvailabilitiesOverviewFilter = {};
    }
    loggedInUser.jobAvailabilitiesOverviewFilter =
      this.route.snapshot.queryParams;
    this.toastService.add({
      type: 'SUCCESS',
      message: 'savedDefaultFilter',
      timeout: 5000,
    });
  }

  private _loadDefaultFilters() {
    let routeQueryParams = this.route.snapshot.queryParams;
    let defaultValues;
    let filterValues;
    const loggedInUser = this.loggedInUser$.value;
    if (loggedInUser && loggedInUser.jobAvailabilitiesOverviewFilter) {
      if (
        loggedInUser.jobAvailabilitiesOverviewFilter &&
        Object.keys(loggedInUser.jobAvailabilitiesOverviewFilter)?.length
      ) {
        defaultValues = loggedInUser.jobAvailabilitiesOverviewFilter;
      }
    }

    if (Object.keys(routeQueryParams).length !== 0) {
      //query param values
      filterValues = routeQueryParams || defaultValues || {};
    } else if (defaultValues) {
      //user default values
      filterValues = defaultValues;
    } else {
      //origin default values
      filterValues = {};
    }
    let extras: any = {
      relativeTo: this.route,
      queryParams: filterValues,
      replaceUrl: true,
      queryParamsHandling: 'merge',
    };
    this.router.navigate([], extras);
  }
}
